import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
} from '@chakra-ui/react';
import { groupBy } from 'lodash';
import { useMemo } from 'react';
import { Property } from '../../domain/property-types';
import { useLocalStorage } from '../../hooks/local-storage';
import PlaceholderText from '../text/PlaceholderText';
import BIMPropertiesAccordionBody from './BIMPropertiesAccordionBody';
import { ProjectSettingsType } from 'src/gql/graphql';

// Selected categories to be hidden by default, to reduce complexity for the user
const HIDDEN_CATEGORIES = [
  'Item',
  'Element ID',
  'Revit Type',
  'Shape',
  'Sparkel Attributes',
];

type PropertiesAccordionProps = {
  properties: Property[];
  projectId: string;
  projectSettings?: ProjectSettingsType;
};

export default function BIMPropertiesTab({
  properties,
  projectId,
  projectSettings,
}: PropertiesAccordionProps) {
  const [expandedCategories, setExpandedCategories] = useLocalStorage<string[]>(
    `${projectId}-bim-properties-tab-expanded-categories`,
    []
  );

  const propertiesForCategories = useMemo(
    () => groupBy(properties, (property) => property.attribute.category),
    [properties]
  );

  const filteredProperties = Object.keys(propertiesForCategories)
    .filter(
      (category) =>
        !(HIDDEN_CATEGORIES.includes(category) || category.includes('__'))
    )
    .sort();

  let expandedIndex: number[] = [];
  for (const category of expandedCategories) {
    const index = filteredProperties.indexOf(category);
    if (index !== -1) {
      expandedIndex.push(index);
    }
  }

  if (filteredProperties.length === 0) {
    return (
      <Box ml={2} pt={2}>
        <PlaceholderText>
          No BIM properties for the selected element
        </PlaceholderText>
      </Box>
    );
  } else {
    return (
      <Accordion
        pl={0}
        variant={'panel-accordion'}
        allowMultiple
        index={expandedIndex}
        onChange={(indexes: number[]) => {
          let newExpandedCategories = new Set<string>();
          for (const i of indexes) {
            newExpandedCategories.add(filteredProperties[i]);
          }
          setExpandedCategories([...newExpandedCategories]);
        }}
      >
        {filteredProperties.map((property) => (
          <AccordionItem key={property}>
            <AccordionButton>
              <Box
                flex="1"
                textAlign="left"
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
              >
                {property}
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={0} pt={0}>
              <BIMPropertiesAccordionBody
                propertiesForCategory={propertiesForCategories[property]}
                projectSettings={projectSettings}
              />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }
}
